import { configMgr } from '@nab/nab-x-sdk-browser';
import {
    start,
    DafInMemoryCacheStrategy,
    DafExchangeStrategy,
    registerSpaComponent,
    SeedTokenProviderWrapperStrategy,
    DafAnonymousAuthStrategy,
    DefaultAuthConfig,
    DafAuthConfig,
    DafGrantType
} from '@nab/x-spa-react';
import { ShellContainer } from './shellContainer';
import { memoize } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import Spa from './Spa';
import axios from 'axios';
import {
    getAuthStrat,
    getBearerToken,
    setApplication,
    setAuthStrat,
    setClientId,
    setEnv,
    setIsMultiLogin
} from 'utils/sessionStorage';
import { makeRumStrategy } from '../../utils/rumStrategy';
import LoginSelector from 'components/LoginSelector/LoginSelector';
import { IB, LOGIN_SELECTOR, NABC } from 'utils/constants';
import { spaContextProvider } from 'utils/helpers';

registerSpaComponent(LOGIN_SELECTOR, LoginSelector);

const isLoginPath = (path?: string) => {
    return path?.includes('/login');
};

const launch = memoize(async (basePath: string, pathname: string, contextName: string, multiLogin: string) => {
    await configMgr.init();
    const envConfig = (await axios.get(`config.json`)).data;

    //set inital strat for miniapp loading, but get actual strat after login selector page.
    let nabTenant = getAuthStrat() ?? IB;
    if (pathname.split('/').pop() === NABC) {
        nabTenant = NABC;
    } else if (pathname.split('/').pop() === IB) {
        nabTenant = IB;
    }
    //get env
    const environment = envConfig.NABX_INNOVATION_SHELL_FRONTEND_ENV || 'local';
    //get auth strat by tenant name (should maybe be a default like in nabx-universal-shell)
    const appConfig = (
        nabTenant !== LOGIN_SELECTOR && (await axios.get(`config/authStrategies/${nabTenant}/context.json`))
    ).data[environment];

    //set session storage
    setEnv(environment);
    setApplication(contextName);
    setIsMultiLogin(multiLogin);
    setAuthStrat(nabTenant);
    setClientId(appConfig.clientId);

    const hostElement = document.createElement('div');
    //auth config from appconfig from context file.
    const authConfig: DefaultAuthConfig & DafAuthConfig = {
        tokenType: appConfig.tokenType,
        tokenSource: appConfig.tokenSource,
        context: appConfig.context,
        environment: appConfig.dafEnvironment,
        requestTokenOnLoad: false,
        scopes: 'idp:user',
        grantType: isLoginPath(pathname) ? DafGrantType.Anonymous : undefined
    };

    await start({
        // The HTML element that the x-spa will render the shell into
        hostElement: hostElement,

        // React component which provides the main layout for the shell
        rootComponent: Spa,

        // SDK v5: shell must supply a UI Container (NUIT createUI + withSpaContainer)
        Container: ShellContainer,

        // Named set of apps and menu items which live in this shell
        rootContextId: 'root',

        // this is the base URL that the shell is being hosted on
        // i.e. /landscape
        serverBasePath: basePath,

        // Default auth config inherited by all apps which don't explicitly override it
        authConfig: authConfig,

        // Auth strategies applicable to this shell (will most likely include the DafTokenExchangeStrategy
        authStrategies: [
            new DafAnonymousAuthStrategy(appConfig.dafEndpoint, appConfig.clientId),
            new SeedTokenProviderWrapperStrategy(() => Promise.resolve(getBearerToken()), {
                context: 'external',
                tokenType: 'Bearer',
                tokenSource: 'DAF'
            }),
            new DafExchangeStrategy(appConfig.dafEndpoint)
        ],

        // // Defines how tokens are cached & validated
        authTokenCacheStrategy: new DafInMemoryCacheStrategy(),

        brand: {
            id: isLoginPath(pathname) ? nabTenant : 'NAB',
            themeName: 'nab'
        },

        userConfig: {
            org: 'NAB',
            type: 'customer'
        },
        spaContextProvider: spaContextProvider(environment, contextName),

        servicesConfig: {
            rum: {
                strategy: makeRumStrategy(environment, appConfig)
            }
        }
    });
    return hostElement;
});

export function Shell(props) {
    const ref = useRef<HTMLDivElement>(null);
    const route = useRouteMatch();
    const location = useLocation();
    const { contextName, multiLogin } = props;
    useEffect(() => {
        if (ref.current) {
            launch(route.path, location.pathname, contextName, multiLogin).then((element) => {
                ref.current.appendChild(element);
            });
        }
    }, [route.path, location.pathname, contextName, multiLogin]);

    return <div ref={ref} />;
}
